import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import InfiniteScroll from 'react-infinite-scroll-component';
import Search from '../common/lookups/search';
import Loader from '../common/loader';
import entityComposition from '../common/classes/entityComposition';
import BreadcrumbsBack from '../common/lookups/breadcrumbsBack';
import CabinetFilterOption from './cabinetFilterOption';
const CabinetFilter = _ref => {
  var _cabinetFilterOptions, _filterOptionsIndexQu;
  let cabinetIdentifier = _ref.cabinetIdentifier,
    _ref$cabinetFilterOpt = _ref.cabinetFilterOptionsActions,
    optionsServerFilter = _ref$cabinetFilterOpt.serverFilter,
    optionsServerIndex = _ref$cabinetFilterOpt.serverIndex,
    pageNext = _ref$cabinetFilterOpt.pageNext,
    _ref$filterDefinition = _ref.filterDefinition,
    index = _ref$filterDefinition.index,
    title = _ref$filterDefinition.title,
    indexViewIndexQuery = _ref.indexQuery,
    onBack = _ref.onBack,
    indexViewServerFilter = _ref.serverFilter,
    _ref$cabinetFilterOpt2 = _ref.cabinetFilterOptions,
    cabinetFilterOptions = _ref$cabinetFilterOpt2 === void 0 ? {} : _ref$cabinetFilterOpt2;
  const _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    scrollPos = _useState2[0],
    setScrollPos = _useState2[1];
  const _useState3 = useState(() => indexViewIndexQuery.filters[index] || []),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedItems = _useState4[0],
    setSelectedItems = _useState4[1];

  // Sync local selection with index filters (clear, URL deep link, saved view, etc.).
  useEffect(() => {
    const raw = indexViewIndexQuery.filters[index];
    const next = Array.isArray(raw) ? raw : [];
    setSelectedItems(prev => {
      if (next.length === 0 && prev.length > 0) {
        return [];
      }
      return _.isEqual(prev, next) ? prev : next;
    });
  }, [index, indexViewIndexQuery.filters]);

  // set defaults for the cabinet-specifc data since rendering happens while
  // server requests are being made
  const currentCabinet = cabinetFilterOptions.currentCabinet,
    currentFilter = cabinetFilterOptions.currentFilter;
  const currentCabinetFilterOptions = ((_cabinetFilterOptions = cabinetFilterOptions[currentCabinet]) === null || _cabinetFilterOptions === void 0 ? void 0 : _cabinetFilterOptions[currentFilter]) || {};
  const error = currentCabinetFilterOptions.error,
    _currentCabinetFilter = currentCabinetFilterOptions.indexQuery,
    filterOptionsIndexQuery = _currentCabinetFilter === void 0 ? {
      filters: {}
    } : _currentCabinetFilter,
    _currentCabinetFilter2 = currentCabinetFilterOptions.list,
    list = _currentCabinetFilter2 === void 0 ? [] : _currentCabinetFilter2,
    _currentCabinetFilter3 = currentCabinetFilterOptions.loading,
    loading = _currentCabinetFilter3 === void 0 ? false : _currentCabinetFilter3;

  // load the filter options
  useEffect(() => {
    // Don't make requests while loading to avoid race conditions and duplicate requests.
    // The loading check must be first to prevent triggering new requests
    // when loading state changes.
    if (loading) {
      return;
    }

    // avoid unnecessary reloading in the same cabinet
    if (error || filterOptionsIndexQuery.cabinet === cabinetIdentifier && filterOptionsIndexQuery.filterIndex === index && !filterOptionsIndexQuery.error && !error) {
      return;
    }
    optionsServerIndex(cabinetIdentifier, index, filterOptionsIndexQuery);
  }, [cabinetIdentifier, currentCabinet, currentFilter, error, filterOptionsIndexQuery, index, loading, optionsServerIndex]);
  const onScroll = event => {
    const newPos = event.target.scrollTop;
    if (newPos !== 0) {
      setScrollPos(newPos);
    }
  };
  const handlePageNext = () => {
    pageNext(cabinetIdentifier, index);
  };
  const optionMatchesSelection = (option, item) => item === option.id || item === option.value;
  const handleCabinetFilter = selection => {
    // all selections are now stored as an array. Nested
    // selections will be an array with multiple values.
    // `id: null` means “No selection” and must stay `null` (do not use `??`, which skips null).
    const selectionKey = selection.id === undefined || selection.id === '' ? selection.value : selection.id;
    const existing = selectedItems.some(_ref2 => {
      let _ref3 = _slicedToArray(_ref2, 1),
        item = _ref3[0];
      return optionMatchesSelection(selection, item);
    });
    const updatedSelections = existing ? selectedItems.filter(_ref4 => {
      let _ref5 = _slicedToArray(_ref4, 1),
        item = _ref5[0];
      return !optionMatchesSelection(selection, item);
    }) : [...selectedItems, [selectionKey]];
    setSelectedItems(updatedSelections);
    indexViewServerFilter(_objectSpread(_objectSpread({}, indexViewIndexQuery.filters), {}, {
      [index]: updatedSelections
    }), indexViewIndexQuery);
  };

  // Memoize handleSearch to prevent unnecessary re-renders and ref updates
  // in child components. This ensures stable callback references.
  const handleSearch = useCallback(filters => {
    // Search returns filters object with "text" property
    optionsServerFilter(cabinetIdentifier, index, filters);
  }, [cabinetIdentifier, index, optionsServerFilter]);
  const isSelected = option => selectedItems.some(_ref6 => {
    let _ref7 = _slicedToArray(_ref6, 1),
      item = _ref7[0];
    return optionMatchesSelection(option, item);
  });

  // don't display the loader during infinite scroll requests for a smoother UX
  const hideLoader = !(loading && (filterOptionsIndexQuery === null || filterOptionsIndexQuery === void 0 || (_filterOptionsIndexQu = filterOptionsIndexQuery.paging) === null || _filterOptionsIndexQu === void 0 ? void 0 : _filterOptionsIndexQu.current) === undefined);
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "text-center"
  }, title), /*#__PURE__*/React.createElement(BreadcrumbsBack, {
    onBack: onBack,
    onSelect: onBack,
    backLabel: "Filters"
  }), /*#__PURE__*/React.createElement(Search, {
    serverFilter: handleSearch,
    indexQuery: filterOptionsIndexQuery,
    loading: loading
  }), /*#__PURE__*/React.createElement("hr", null), /*#__PURE__*/React.createElement(Loader, {
    loaded: hideLoader
  }, /*#__PURE__*/React.createElement("div", {
    id: "cabinetfilter-scroll",
    style: {
      height: '250px',
      overflowX: 'scroll'
    },
    role: "scrollbar",
    "aria-controls": "cabinetfilter-scroll",
    "aria-valuenow": 0
  }, /*#__PURE__*/React.createElement(InfiniteScroll, {
    dataLength: list.length || 0,
    next: handlePageNext,
    scrollableTarget: "cabinetfilter-scroll",
    style: {
      overflow: 'hidden'
    },
    initialScrollY: scrollPos,
    onScroll: onScroll,
    hasMore: true
  }, /*#__PURE__*/React.createElement(CabinetFilterOption, {
    key: "null",
    option: {
      id: null,
      name: 'No selection'
    },
    onSelect: handleCabinetFilter,
    selected: isSelected({
      id: null,
      name: 'No selection'
    })
  }), list.map(option => /*#__PURE__*/React.createElement(CabinetFilterOption, {
    key: option.id,
    option: option,
    onSelect: handleCabinetFilter,
    selected: isSelected(option)
  }))))));
};
CabinetFilter.propTypes = {
  cabinetFilterOptions: PropTypes.shape({
    currentCabinet: PropTypes.string,
    currentFilter: PropTypes.string
  }),
  // these cabinet actions are for populating the options available for the current filter.
  cabinetFilterOptionsActions: PropTypes.shape({
    serverFilter: PropTypes.func,
    serverIndex: PropTypes.func,
    pageNext: PropTypes.func
  }).isRequired,
  cabinetIdentifier: PropTypes.string.isRequired,
  filterDefinition: PropTypes.shape({
    index: PropTypes.string,
    title: PropTypes.string
  }).isRequired,
  // the current cabinet's indexQuery
  indexQuery: PropTypes.shape({
    filters: PropTypes.shape({
      weekEnding: PropTypes.arrayOf(PropTypes.shape({}))
    })
  }).isRequired,
  onBack: PropTypes.func.isRequired,
  // The serverFilter property is responsible for filtering the cabinet index view and is
  // managed by cabinetFilterPanel.jsx
  serverFilter: PropTypes.func.isRequired
};
export { CabinetFilter as TestCabinetFilter };
export default entityComposition(CabinetFilter, ['cabinetFilterOptions'], {}, []);